import React, { useCallback, useEffect } from 'react';
import { CommentAsyncStatus, IComment } from '@shared/models/comment.model';
import { SxProps, Tooltip } from '@mui/material';
import { CommentInput } from './CommentInput';
import { CommentRowBase } from './CommentRowBase';
import { useCommentsStore } from '@client/store/comment/comment.store';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import { usePromiseSnackbar } from '@client/hooks/use-snackbar.hooks';
import DateText from '@ui/DateText';
import { setDraft } from '@client/store/comment/comments.hook';
import { NodonTheme } from '@client/style';
import { CommentLoader } from './CommentLoader';
import {
  useIsReadonly,
  useUser,
  useUserLookup,
} from '@client/hooks/use-user.hooks';
import { Duration } from 'luxon';
import { OPEN_AI_USER } from '@shared/constants';
import { User } from '@shared/models/user.model';
import { CommentAsyncError } from './CommentAsyncError';
import { useAbstractCommentAsyncStatus } from './use-abstract-comment-status.hook';
import { IconButton } from '@ui/buttons';

interface CommentProps {
  comment: IComment;
}

const otherUsersLookup: Record<string, User> = {
  [OPEN_AI_USER.id]: OPEN_AI_USER,
};

const Comment: React.FC<CommentProps> = ({ comment }) => {
  const confirm = useIsConfirmed();
  const promiseSnackbar = usePromiseSnackbar();

  const { updateComment, deleteComment, setEditingId, editingId } =
    useCommentsStore(
      ({ updateComment, deleteComment, setEditingId, editingId }) => ({
        updateComment,
        deleteComment,
        setEditingId,
        editingId,
      }),
    );

  const user = useUser();
  const usersLookup = useUserLookup();
  const readonly = useIsReadonly();

  const [iconBtnVisible, showIconBtn, hideIconBtn] = useBooleanState();

  const owner: User | undefined =
    usersLookup[comment.owner] ?? otherUsersLookup[comment.owner];

  const isEditing = comment.id === editingId;

  const edit = useCallback(
    (message: string) => {
      setEditingId(undefined);
      updateComment({ ...comment, message });
    },
    [comment, setEditingId, updateComment],
  );

  const handleIconClick = useCallback(async () => {
    if (!isEditing) {
      setEditingId(comment.id);
      return;
    }

    const confirmed = await confirm({
      title: 'Delete comment?',
      description: 'This action cannot be undone',
      confirmationText: 'Delete',
    });

    if (confirmed) {
      promiseSnackbar(deleteComment(comment.id));
      setDraft(comment.id, undefined);
    }
  }, [
    comment,
    confirm,
    deleteComment,
    isEditing,
    promiseSnackbar,
    setEditingId,
  ]);

  const handleCommentHover = useCallback(() => {
    if (!readonly && user?.id === comment.owner) {
      showIconBtn();
    }
  }, [comment.owner, showIconBtn, user?.id, readonly]);

  useEffect(() => {
    if (!editingId) hideIconBtn();
  }, [editingId, hideIconBtn]);

  const abstractStatus = useAbstractCommentAsyncStatus(
    comment,
    Duration.fromObject({ minutes: 2 }).as('milliseconds'),
  );

  if (owner === undefined) return null;

  switch (abstractStatus) {
    case CommentAsyncStatus.Pending:
      return <CommentLoader user={owner} comment={comment} />;
    case CommentAsyncStatus.Error:
      return <CommentAsyncError user={owner} comment={comment} />;
    case CommentAsyncStatus.Resolved:
      return (
        <CommentRowBase
          user={owner}
          subtitle={
            <DateText
              date={comment.updated_at}
              color={NodonTheme.palette.neutral.light}
              variant="caption"
            />
          }
          leftContent={
            iconBtnVisible && (
              <Tooltip title={(isEditing ? 'Delete' : 'Edit') + ' comment'}>
                <IconButton
                  category="mui"
                  sx={iconButtonSx}
                  icon={isEditing ? 'Delete' : 'Edit'}
                  onClick={handleIconClick}
                />
              </Tooltip>
            )
          }
          rightContent={
            <CommentInput
              id={comment.id}
              message={comment.message}
              onSave={edit}
            />
          }
          containerProps={{
            onMouseEnter: handleCommentHover,
            onMouseLeave: hideIconBtn,
          }}
        />
      );
  }
};

const iconButtonSx: NonNullable<SxProps> = {
  padding: 1.25,
  mt: 0.75,
} as const;

export default Comment;
